// src/App.jsx
import React, { Suspense, lazy } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import ComingSoon from './components/ComingSoon';

// ========================================
// SITE STATUS
// ========================================
// The public SPLASH! platform is still behind a coming-soon page. This is now
// env-driven rather than a hardcoded constant, so flipping it is a deploy-time
// decision (set VITE_SITE_LIVE=true) rather than a code edit.
//
// It defaults to FALSE — an unset or malformed value keeps the public page up,
// which is the safe direction to fail in.
const SITE_LIVE = import.meta.env.VITE_SITE_LIVE === 'true';

// ========================================
// SURFACES THAT STAY REACHABLE WHILE THE SITE IS DARK
// ========================================
// Two surfaces are safe to expose behind the coming-soon page, because in both
// cases the route existing is not access — each is gated on real identity and
// on firestore.rules, not on the URL being secret:
//
//   /admin    — the moderation and admin dashboards (invitation-granted).
//   /circles  — the VIBE Experiment parents' circles. Invite + email-link
//               sign-in; rules refuse anonymous sessions for every
//               circle-scoped read.
//
// Neither implies a public launch. Nothing on the coming-soon page links to
// either; founding parents arrive on an invite URL we email them.
const DARK_SITE_ALLOWED_PREFIXES = ['/admin', '/circles'];

function currentPathAllowedWhileDark() {
  if (typeof window === 'undefined') return false;
  return DARK_SITE_ALLOWED_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix)
  );
}

function isAdminPath() {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/admin');
}

// Only import full app dependencies when they're actually needed. This keeps
// the coming-soon bundle small and stops incomplete components from breaking
// the public page.
const FullApp = lazy(() => import('./FullApp'));
const AdminPortal = lazy(() => import('./components/admin/AdminPortal'));

const AdminFallback = () => (
  <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-splash-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  // The admin portal renders standalone (it has no need for the app router),
  // so it is checked first and served whether or not the site is live.
  if (isAdminPath()) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<AdminFallback />}>
          <AdminPortal />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (!SITE_LIVE && !currentPathAllowedWhileDark()) {
    return (
      <ErrorBoundary>
        <ComingSoon />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-white/70">Loading SPLASH!...</p>
          </div>
        </div>
      }>
        <FullApp />
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
